import {
    Dialog as MaterialDialog,
    DialogActions,
    DialogContent,
    DialogProps as MaterialDialogProps,
    DialogTitle,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/CloseRounded";
import clsx from "clsx";
import lotusIcon from "images/icons/lotus-dark.svg";
import _ from "lodash";
import * as React from "react";
import xiconblack from "images/icons/xiconblack.svg";
import { useBreakpointQuery } from "hooks/useBreakPointQuery";
import { useModalNavigation } from "../hooks/useModalNavigation";

const useStyles = makeStyles((theme) => ({
    dialog: {},
    dialogContainer: {
        padding: theme.spacing(8, 6, 6, 6),
        background: "none",

        "& > :first-child": {
            marginTop: 0,
        },
    },

    lotusIcon: {
        textAlign: "right",
        marginBottom: theme.spacing(-2),
        opacity: 0.3,
    },

    closeIcon: {
        position: "absolute",
        top: 8,
        right: 8,
        zIndex: 1,
    },

    closeIconLarge: {
        "& .MuiSvgIcon-root": {
            width: "1.5em",
            height: "1.5em",
            fontSize: "1.5em",
        },
    },
}));

export type CSDialogProps = {
    children: React.ReactNode | React.ReactNode[];
    closeButtonSize?: "large" | "small" | "large-thin";
    onClose?: (ev: React.MouseEvent<any>) => void;
    open: boolean;
    withLotus?: boolean;
    classes?: Partial<ReturnType<typeof useStyles>>;
    transitionDuration?: number;
    dialogClasses?: MaterialDialogProps["classes"];
    title?: React.ReactNode;
    actions?: React.ReactNode;
    alignTitle?: "inherit" | "right" | "left" | "center" | "justify";
} & MaterialDialogProps;

export const CSDialog = (props: CSDialogProps) => {
    const classes = useStyles(props);
    const isExtraSmallScreen = useBreakpointQuery("xs");
    const navigation = useModalNavigation();

    const {
        children,
        closeButtonSize = "large",
        onClose = () => navigation.goBack(),
        open,
        withLotus = false,
        dialogClasses,
        title,
        actions,
        alignTitle,
        ...others
    } = props;

    const closeIconClasses = clsx(classes.closeIcon, {
        [classes.closeIconLarge]: closeButtonSize === "large",
    });

    return (
        <MaterialDialog
            className={classes.dialog}
            onClose={onClose}
            open={open}
            fullWidth
            maxWidth="md"
            data-testid="modal"
            transitionDuration={props.transitionDuration}
            classes={dialogClasses}
            {..._.omit(others, "classes")}
        >
            <IconButton
                color="secondary"
                className={closeIconClasses}
                aria-label="close"
                onClick={onClose}
                data-testid="closeModalButton"
            >
                {closeButtonSize === "large-thin" && !isExtraSmallScreen ? (
                    <img src={xiconblack} alt="" />
                ) : (
                    <CloseIcon />
                )}
            </IconButton>

            {title && <DialogTitle align={alignTitle}>{title}</DialogTitle>}

            <DialogContent className={classes.dialogContainer}>
                {children}
                {withLotus && (
                    <div className={classes.lotusIcon}>
                        <img src={lotusIcon} alt="Lotus icon" />
                    </div>
                )}
            </DialogContent>

            {actions && <DialogActions>{actions}</DialogActions>}
        </MaterialDialog>
    );
};
